import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { faClock, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Project } from '../../../core/services/project.service';
import { Task } from '../../../core/services/task.service';
import { normalizeTypedTimeInput, shouldUseTypedTimeInput } from '../../../core/utils/time-input';

export interface TimeEntryCreateData {
  projects: Project[];
  tasks: Task[];
  tagsFeatureEnabled: boolean;
}

export interface TimeEntryCreateResult {
  projectId?: string;
  taskId?: string;
  description?: string;
  startTime: string;
  endTime: string;
  durationSeconds: number;
  isBillable: boolean;
  tags?: string[];
}

@Component({
  selector: 'app-time-entry-create-dialog',
  templateUrl: './time-entry-create-dialog.component.html',
  styleUrls: ['./time-entry-create-dialog.component.scss'],
  standalone: false
})
export class TimeEntryCreateDialogComponent implements OnInit {
  createForm: FormGroup;
  filteredTasks: Task[] = [];
  useTypedTimeInput = shouldUseTypedTimeInput();
  faClock = faClock;
  faPlus = faPlus;

  constructor(
    public dialogRef: MatDialogRef<TimeEntryCreateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TimeEntryCreateData,
    private fb: FormBuilder
  ) {
    const now = new Date();
    const end = new Date(now);
    end.setMinutes(0, 0, 0);
    const start = new Date(end.getTime() - 60 * 60 * 1000);

    this.createForm = this.fb.group({
      date: [start, Validators.required],
      startTime: [this.formatTime(start), Validators.required],
      endTime: [this.formatTime(end), Validators.required],
      projectId: [''],
      taskId: [''],
      description: [''],
      isBillable: [true],
      tagsText: ['']
    });
  }

  ngOnInit(): void {
    this.createForm.get('projectId')?.valueChanges.subscribe((projectId: string) => {
      this.filteredTasks = this.data.tasks.filter(task => task.projectId === projectId);
      this.createForm.patchValue({ taskId: '' }, { emitEvent: false });
      this.syncTaskControlState();

      const project = this.data.projects.find(item => item.id === projectId);
      const billableControl = this.createForm.get('isBillable');
      if (project?.isBillable === false) {
        billableControl?.setValue(false, { emitEvent: false });
        billableControl?.disable({ emitEvent: false });
      } else {
        billableControl?.enable({ emitEvent: false });
      }
    });
    this.syncTaskControlState();
  }

  private syncTaskControlState(): void {
    const taskControl = this.createForm.get('taskId');
    if (this.filteredTasks.length === 0) {
      taskControl?.disable({ emitEvent: false });
    } else {
      taskControl?.enable({ emitEvent: false });
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  create(): void {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }

    const value = this.createForm.getRawValue();
    const normalizedStartTime = this.normalizeTimeField('startTime');
    const normalizedEndTime = this.normalizeTimeField('endTime');
    if (!normalizedStartTime || !normalizedEndTime) {
      return;
    }
    const start = this.combineDateTime(value.date, normalizedStartTime);
    let end = this.combineDateTime(value.date, normalizedEndTime);
    if (end.getTime() <= start.getTime()) {
      end = new Date(end);
      end.setDate(end.getDate() + 1);
    }

    const durationSeconds = Math.floor((end.getTime() - start.getTime()) / 1000);
    const tags = this.parseTags(value.tagsText);

    this.dialogRef.close({
      projectId: value.projectId || undefined,
      taskId: value.taskId || undefined,
      description: value.description?.trim() || undefined,
      startTime: start.toISOString(),
      endTime: end.toISOString(),
      durationSeconds,
      isBillable: value.isBillable !== false,
      tags: tags.length > 0 ? tags : undefined
    } satisfies TimeEntryCreateResult);
  }

  private combineDateTime(date: Date, timeString: string): Date {
    const [hours, minutes] = timeString.split(':').map(Number);
    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);
    return combined;
  }

  private formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private parseTags(tagsText: string): string[] {
    return (tagsText || '')
      .split(',')
      .map(tag => tag.trim().toLowerCase())
      .filter(Boolean);
  }

  private normalizeTimeField(controlName: 'startTime' | 'endTime'): string | null {
    const control = this.createForm.get(controlName);
    const value = String(control?.value ?? '');
    const normalized = this.useTypedTimeInput ? normalizeTypedTimeInput(value) : value;
    if (!normalized) {
      control?.setErrors({ ...(control.errors || {}), invalidTime: true });
      control?.markAsTouched();
      return null;
    }

    control?.setValue(normalized, { emitEvent: false });
    if (control?.errors) {
      const { invalidTime, ...otherErrors } = control.errors;
      control.setErrors(Object.keys(otherErrors).length > 0 ? otherErrors : null);
    }
    return normalized;
  }
}
